import { array, fn } from '@ember/helper';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { PhCloudArrowUp } from 'ember-phosphor-icons';
import Select from 'tio-reimagined/components/select';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import ApproverComments from 'tio-common/components/tuition-assistance/approver-comments';
import AttachmentList from 'tio-common/components/tuition-assistance/forms/upload-attachments/attachment-list';
import Component from '@glimmer/component';
import TuitionAssistanceApplicationDetailsApproverOverview from 'tio-common/components/tuition-assistance/application-details/approver-overview';
import TuitionAssistanceApplicationDetailsEmployeeInformation from 'tio-common/components/tuition-assistance/application-details/employee-information';
import TuitionAssistanceFormsUploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import TuitionAssistanceProgramDetailsAppDetailsAppHistory from 'tio-common/components/tuition-assistance/program-details/app-details/app-history';
import TuitionAssistanceProgramDetailsApplicationInformation from 'tio-common/components/tuition-assistance/program-details/application-information';
import TuitionAssistanceProgramDetailsProgramInfoCard from 'tio-common/components/tuition-assistance/program-details/program-info-card';
import type SessionContextService from 'tio-employee/services/session-context';
import type TasProgramInstanceModel from 'tio-common/models/tas-program-instance';

interface RouteSignature {
  Args: {
    model: TasProgramInstanceModel;
  };
}

const classificationOptions = [
  { label: 'Program Approval Documents', key: 'PROGRAM_APPROVAL_DOCUMENTS' },
  { label: 'Program Completion Documents', key: 'COMPLETION_DOCUMENTS' },
];

export default class TuitionAssistanceProgramInstancesShowReadonlyRouteComponent extends Component<RouteSignature> {
  @service declare sessionContext: SessionContextService;
  @tracked uploadModalOpen = false;
  @tracked selectedClassification = 'PROGRAM_APPROVAL_DOCUMENTS';
  @tracked uploadClassificationField = 'PROGRAM_APPROVAL_DOCUMENTS';

  get allAttachments() {
    return this.args.model.tasAssets || [];
  }

  setUploadClassification = (classification: string | null) => {
    this.selectedClassification = classification ?? '';
    this.uploadClassificationField = classification || 'PROGRAM_APPROVAL_DOCUMENTS';
  };

  <template>
    <HStack>
      <Section>
        <TuitionAssistanceApplicationDetailsEmployeeInformation
          @instance={{@model}}
          @profileRoute="authenticated.admin.employees.show"
          @routeModel={{@model.employee.id}}
        />
        <TuitionAssistanceProgramDetailsApplicationInformation
          @application={{@model}}
          @isAdminView={{true}}
          @canRequestRepayment={{this.sessionContext.user.isAccountOwnerOrEmployeeAdmin}}
          @isReadonlyView={{true}}
          @requestingApp="employee"
        />
        <TuitionAssistanceProgramDetailsProgramInfoCard
          @programInstance={{@model}}
          @showAttachments={{true}}
        />
      </Section>
      <Section>
        <VStack @collapsed={{true}}>
          <ApproverComments
            @commentable={{@model}}
            @commentableType="TasProgramInstance"
            @authoredByPerson={{this.sessionContext.user.person}}
            @commentTags={{array "approver"}}
          />
          <TuitionAssistanceApplicationDetailsApproverOverview
            @programTemplate={{@model.tasProgramTemplate}}
          />
        </VStack>
      </Section>
    </HStack>

    <Section>
      <:header>
        {{t "tuition_assistance.program_details.attachments"}}
      </:header>
      <:actions>
        <button type="button" {{on "click" (fn (mut this.uploadModalOpen) true)}}>
          <PhCloudArrowUp @size="28" class="mb-2 text-ocean-600" />
        </button>
      </:actions>
      <:body>
        {{#if this.allAttachments.length}}
          <AttachmentList @assets={{this.allAttachments}} @disabled={{true}} class="w-full" />
        {{else}}
          <p class="text-sm text-tio-gray-500">
            {{t "tuition_assistance.program_details.no_attachments"}}
          </p>
        {{/if}}
      </:body>
    </Section>

    <TuitionAssistanceProgramDetailsAppDetailsAppHistory
      @application={{@model}}
      @viewType="admin"
    />

    <Modal @isOpen={{this.uploadModalOpen}} @onClose={{fn (mut this.uploadModalOpen) false}}>
      <:header>{{t "tuition_assistance.program_details.upload_attachments"}}</:header>
      <:body>
        <label for="classification-type" class="font-semibold text-sm text-tio-gray-700 block mb-1">
          {{t "tuition_assistance.program_details.courses.document_type"}}
        </label>
        <Select
          data-test-classification-type
          @items={{classificationOptions}}
          @selected={{this.selectedClassification}}
          @onChange={{this.setUploadClassification}}
        />
        <TuitionAssistanceFormsUploadAttachments
          @programTemplate={{@model.tasProgramTemplate}}
          @classification={{this.uploadClassificationField}}
          @attachable={{@model}}
          @attachableType="tasProgramInstance"
          @disabled={{false}}
          @hideAttachments={{true}}
        />
      </:body>
    </Modal>
  </template>
}
